/**
 * DataSyncPage - "Build Data Model"
 *
 * Entry point: ConnectorsPage → "Build Data Model" button → /data-sync
 *
 * Shows all active connectors.  For each connector the user can verify /
 * configure which tables / resources are selected.  Once any connector has
 * something selected the user can start a sync job that runs:
 *   Refresh Schema → Profile Tables → Build Data Model
 * over the connectors that have a selection. A connector with nothing
 * selected is left out of the job and the page says it is skipped: an
 * unconfigured HubSpot must not hold up a model built from the warehouse.
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Paper,
  TextField,
  Divider,
  IconButton,
  Checkbox,
  Tooltip,
  Link,
  Skeleton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppDialog } from '../components/dialog';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SyncRounded from '@mui/icons-material/SyncRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import ArrowForwardIosRounded from '@mui/icons-material/ArrowForwardIosRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import { ReactComponent as GoogleDriveSvg } from '../assets/connectors/google-drive.svg';
import { ReactComponent as PostgresSvg } from '../assets/connectors/postgres.svg';
import { ReactComponent as MySQLSvg } from '../assets/connectors/mysql.svg';
import { ReactComponent as OneDriveSvg } from '../assets/connectors/onedrive.svg';
import { ReactComponent as SharePointSvg } from '../assets/connectors/sharepoint.svg';
import { ReactComponent as GoogleAdsSvg } from '../assets/connectors/google-ads.svg';
import { ReactComponent as BigQuerySvg } from '../assets/connectors/bigquery.svg';
import { ReactComponent as SnowflakeSvg } from '../assets/connectors/Snowflake.svg';
import { ReactComponent as RedshiftSvg } from '../assets/connectors/redshift.svg';
import { ReactComponent as ShopifySvg } from '../assets/connectors/shopify.svg';
import { ReactComponent as MCPSvg } from '../assets/connectors/mcp.svg';
import { buildApiUrl } from '../config';
import { useClaimGate } from './onboarding/claim/ClaimGateContext';
import { SnackbarContext } from '../ui/SnackbarProvider';
import DriveFilesPickerModal, { DriveFile } from '../components/DriveFilesPickerModal';
import { formatRelativeTime } from '../utils/date';
import { useProductOnboarding } from '../hooks/useProductOnboarding';
import OnboardingCoachmark from '../components/OnboardingCoachmark';
import { SyncJobContext } from '../ui/SyncJobProvider';
import {
  deriveExclusions,
  effectiveSelection,
  nextExclusions,
  includesAllTables,
  supportsAutoInclude,
} from '../utils/tableSelection';

// ---------------------------------------------------------------------------
// Connector icon map
// ---------------------------------------------------------------------------

const CONNECTOR_ICON_MAP: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  postgres:     PostgresSvg,
  mysql:        MySQLSvg,
  google_drive: GoogleDriveSvg,
  snowflake:    SnowflakeSvg,
  redshift:     RedshiftSvg,
  onedrive:     OneDriveSvg,
  sharepoint:   SharePointSvg,
  google_ads:   GoogleAdsSvg,
  bigquery:     BigQuerySvg,
  shopify:      ShopifySvg,
  mcp:          MCPSvg,
};

export function ConnectorIcon({ type, size = 24 }: { type: string; size?: number }) {
  const IconSvg = CONNECTOR_ICON_MAP[type];
  return (
    <Box
      sx={(t) => ({
        width: size + 10, height: size + 10, borderRadius: `${t.ds.radius.sm}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: t.tokens.surface.raised,
        flexShrink: 0,
      })}
    >
      {IconSvg
        ? <IconSvg width={size} height={size} />
        : <StorageRounded sx={{ fontSize: size, color: 'text.disabled' }} />}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface ConnectorFull {
  connector_id: string;
  name: string | null;
  type: string;
  category: string; // 'sql' | 'files' | 'rest'
  status: string;
  last_schema_fetch: string | null;
  /**
   * What this connector would actually sync, resolved against the live
   * source. Present only for active SQL connectors on a list fetched with
   * `?include_table_selection=true`, and absent when the source could not be
   * reached — in which case the saved metadata is all we have to go on.
   */
  table_selection?: { available: number; selected: number; excluded: number } | null;
  connector_metadata?: {
    selected_table_names?: string[];
    /**
     * Tables the user deliberately unchecked. Its presence is what marks a
     * SQL connector as configured; everything not listed here is included,
     * including tables added to the source later. See utils/tableSelection.
     */
    excluded_table_names?: string[];
    selected_drive_files?: {
      id: string;
      name: string;
      mimeType?: string;
      /** Optional. When omitted, the file's full sheet/tab list is synced. */
      selected_sheets?: string[] | null;
    }[];
  };
}

export type StepStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface SubSubStep {
  id: string; label: string; status: StepStatus; error?: string | null;
}
export interface SubStep {
  id: string; label: string; status: StepStatus; error?: string | null; substeps?: SubSubStep[];
}
export interface Step {
  id: string; label: string; status: StepStatus; error?: string | null; substeps?: SubStep[];
}
export interface SyncJob {
  job_id: string;
  tenant_id: string;
  connector_ids: string[];
  status: string;
  progress: { steps: Step[] } | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

// ---------------------------------------------------------------------------
// Shared helpers (re-exported for DataSyncJobPage)
// ---------------------------------------------------------------------------

export function StatusChip({ status }: { status: string }) {
  const map: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'error' }> = {
    pending: { label: 'Pending', color: 'default' },
    running: { label: 'Running', color: 'primary' },
    completed: { label: 'Completed', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
  };
  const cfg = map[status] || { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" variant={status === 'running' ? 'filled' : 'outlined'} />;
}

export function parseUtcDate(input: string): Date {
  if (input.includes('T') && !input.endsWith('Z') && !input.includes('+') && !input.split('T')[1]?.includes('-')) {
    return new Date(input + 'Z');
  }
  return new Date(input);
}

export function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/** Returns count of selected items for a connector, or 0 */
function selectionCount(conn: ConnectorFull): number {
  if (conn.category === 'files') {
    return conn.connector_metadata?.selected_drive_files?.length ?? 0;
  }
  return conn.connector_metadata?.selected_table_names?.length ?? 0;
}

/**
 * One connector's selection, as the page displays it.
 *
 * `total` is null when there is no live count to show — file/REST connectors,
 * or a SQL source we couldn't reach.
 */
interface Summary {
  total: number | null;
  count: number;
  excluded: number;
  allTables: boolean;
  ready: boolean;
}

/** Shared styling for the Total / Selected / Excluded badges. */
const countChipSx = (t: any) => ({
  flexShrink: 0,
  height: 20,
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'text.secondary',
  bgcolor: t.tokens.surface.sunken,
  borderColor: t.tokens.border.subtle,
  '& .MuiChip-label': { px: 0.875 },
});

/**
 * Total · Selected · Excluded, as shown on the connector list and again in
 * the build confirmation.
 *
 * One component so the two can't drift — a build that says something
 * different from the row that led to it is exactly the surprise this is
 * meant to prevent. Falls back to a single badge when there is no live count:
 * file/REST connectors, or a source we couldn't reach.
 */
function SelectionChips({
  summary,
  label,
  layout = 'inline',
}: {
  summary: Summary;
  label: string;
  /**
   * 'inline' sits the badges next to the connector name at their natural
   * width. 'grid' gives each one a fixed column so they line up down a list
   * however long the names or the numbers are.
   */
  layout?: 'inline' | 'grid';
}) {
  const { total, count, excluded, allTables, ready } = summary;
  const grid = layout === 'grid';
  const cellSx = grid ? { width: '100%' } : {};

  if (total === null) {
    if (!ready) return grid ? <Box /> : null;
    return (
      <Chip
        label={allTables ? `All ${label}` : `${count} ${count === 1 ? label.replace(/s$/, '') : label}`}
        size="small"
        variant="outlined"
        sx={(t) => ({
          ...countChipSx(t),
          // Right edge on the Selected badge's, like the rows with live counts
          // (Total, Selected, and an Excluded slot most rows leave empty).
          ...(grid ? { gridColumn: '1 / 3', justifySelf: 'end' } : {}),
          color: 'success.dark',
          bgcolor: t.tokens.semantic.successSoft ?? t.tokens.surface.sunken,
        })}
      />
    );
  }
  return (
    <>
      <Tooltip title={`${total} ${label} in this source`}>
        <Chip label={`Total ${total}`} size="small" variant="outlined"
          sx={(t) => ({ ...countChipSx(t), ...cellSx })} />
      </Tooltip>
      <Tooltip title={count > 0
        ? `${count} ${label} go into the data model`
        : `No ${label} selected, so this connector is skipped when the data model is built`}>
        <Chip
          label={`Selected ${count}`}
          size="small"
          variant="outlined"
          sx={(t) => ({
            ...countChipSx(t),
            ...cellSx,
            color: count > 0 ? 'success.dark' : 'warning.dark',
            bgcolor: count > 0
              ? (t.tokens.semantic.successSoft ?? t.tokens.surface.sunken)
              : (t.tokens.semantic.warningSoft ?? t.tokens.surface.sunken),
          })}
        />
      </Tooltip>
      {excluded > 0 ? (
        <Tooltip title={`${excluded} ${label} you've excluded. They stay out of every build until you select them again.`}>
          <Chip label={`Excluded ${excluded}`} size="small" variant="outlined"
            sx={(t) => ({ ...countChipSx(t), ...cellSx })} />
        </Tooltip>
      ) : grid ? (
        // Hold the third column open so Total and Selected stay put on rows
        // that have nothing excluded.
        <Box />
      ) : null}
    </>
  );
}

/**
 * How many tables the user has deliberately excluded.
 *
 * Read straight from the metadata the connector list already returns, so the
 * row can show "5 tables · 2 excluded" without querying the source.
 */
function exclusionCount(conn: ConnectorFull): number {
  return conn.connector_metadata?.excluded_table_names?.length ?? 0;
}

/**
 * A connector is ready to build when it has something to sync.
 *
 * A SQL connector nobody has configured yet includes every table by default,
 * so it is ready without a saved count — we deliberately don't query the
 * source on page load just to put a number on it.
 */
function isReady(conn: ConnectorFull): boolean {
  return includesAllTables(conn) || selectionCount(conn) > 0;
}

function selectionLabel(conn: ConnectorFull): string {
  if (conn.category === 'files') return 'files';
  if (conn.category === 'rest') return 'resources';
  return 'tables';
}

// ---------------------------------------------------------------------------
// Inline table / resource picker modal
// ---------------------------------------------------------------------------

interface Resource { id: string; name: string; mimeType?: string; type?: string; }

/**
 * Sticky heading for one column of the picker.
 *
 * Stays put while its own column scrolls, so with a hundred tables you can
 * still tell which side you're looking at.
 */
function PaneHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <Box
      sx={(t) => ({
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: t.tokens.surface.raised ?? t.palette.background.paper,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        // Matches the hint + search row above, so the two columns and the
        // dialog header all share one left edge.
        px: { xs: 2, sm: 3 },
        py: 1,
      })}
    >
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', display: 'block' }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.disabled"
        sx={{ fontSize: '0.75rem', display: 'block', lineHeight: 1.4 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

/** Quiet placeholder for a column with nothing in it. */
function PaneEmpty({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.disabled"
      sx={{ display: 'block', px: 2, py: 3, textAlign: 'center', fontSize: '0.75rem' }}>
      {children}
    </Typography>
  );
}

function TablePickerModal({
  open,
  connector,
  tenantId,
  authHeader,
  onClose,
  onSaved,
}: {
  open: boolean;
  connector: ConnectorFull | null;
  tenantId: string;
  authHeader: () => Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const { notify } = React.useContext(SnackbarContext);
  const [resources, setResources] = React.useState<Resource[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  // Exclusions as they stood when the modal opened. Kept so entries naming
  // tables that no longer exist survive a save instead of being quietly
  // forgotten — see nextExclusions().
  const [priorExcluded, setPriorExcluded] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!open || !connector) return;
    setError(null);
    setSearch('');
    setLoading(true);
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connector.connector_id}/resources`), {
      headers: authHeader() as HeadersInit,
    })
      .then(r => { if (!r.ok) throw new Error('Could not fetch resources'); return r.json(); })
      .then((data: Resource[]) => {
        setResources(data || []);
        if (supportsAutoInclude(connector.category)) {
          // Everything the database has right now, minus what the user
          // previously unchecked — so tables added to the source since their
          // last visit show up already checked.
          const ids = (data || []).map(r => r.id);
          const excluded = deriveExclusions(ids, connector.connector_metadata);
          setPriorExcluded(excluded);
          setSelectedIds(effectiveSelection(ids, excluded));
        } else {
          // Drive files and REST resources keep the saved-snapshot behaviour.
          const saved = connector.category === 'files'
            ? (connector.connector_metadata?.selected_drive_files || []).map((f: any) => f.id)
            : (connector.connector_metadata?.selected_table_names || []);
          setPriorExcluded([]);
          setSelectedIds(saved);
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, connector?.connector_id]);

  const label = connector ? selectionLabel(connector) : 'items';
  const isDrive = connector?.category === 'files';
  const autoInclude = supportsAutoInclude(connector?.category);

  /**
   * What a resource is called on screen.
   *
   * For SQL, `id` is the fully-qualified "schema.table" — the form the
   * selection is saved in, and the one that tells two same-named tables in
   * different schemas apart. Adapters disagree about `name`: most set it
   * qualified, Redshift sets it bare. Drive is the exception, where `id` is
   * an opaque Google file id and `name` is the filename.
   *
   * Display and search only — selection, saving and exclusions all key off
   * `r.id` regardless.
   */
  const resourceLabel = (r: Resource) =>
    (autoInclude ? (r.id || r.name) : (r.name || r.id)) || '';

  const filtered = resources.filter(r => {
    if (!search) return true;
    return resourceLabel(r).toLowerCase().includes(search.toLowerCase());
  });

  // Under auto-include the list reads as two parts: what will sync, and —
  // pushed to the bottom — what the user has excluded, so their removals stay
  // visible and re-checkable without crowding the main list.
  const selectedSet = new Set(selectedIds);
  const included = autoInclude ? filtered.filter(r => selectedSet.has(r.id)) : filtered;
  const excludedItems = autoInclude ? filtered.filter(r => !selectedSet.has(r.id)) : [];

  // Group by schema prefix only for SQL tables ("public.users") - never for Drive files
  const groups: Record<string, Resource[]> = {};
  included.forEach(r => {
    const name = resourceLabel(r);
    const dot = name.indexOf('.');
    const schema = (!isDrive && dot > 0) ? name.substring(0, dot) : '__all__';
    if (!groups[schema]) groups[schema] = [];
    groups[schema].push(r);
  });
  const schemaKeys = Object.keys(groups).sort();
  // Derived from every visible row, not just the included ones, so the two
  // sections agree on whether names carry a schema prefix.
  const allSchemaKeys = new Set(
    filtered.map(r => {
      const name = resourceLabel(r);
      const dot = name.indexOf('.');
      return (!isDrive && dot > 0) ? name.substring(0, dot) : '__all__';
    }),
  );
  const hasSchemas = allSchemaKeys.size > 1 || !allSchemaKeys.has('__all__');

  const toggle = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSave = async () => {
    if (!connector) return;
    setSaving(true);
    try {
      const patchBody = isDrive
        ? { connector_metadata: { selected_drive_files: resources.filter(r => selectedIds.includes(r.id)).map(r => ({ id: r.id, name: r.name, mimeType: r.mimeType })) } }
        : autoInclude
          // Exclusions are the durable record of intent; selected_table_names
          // is the materialised result the backend's sync steps read.
          ? { connector_metadata: {
                selected_table_names: selectedIds,
                excluded_table_names: nextExclusions(resources.map(r => r.id), selectedIds, priorExcluded),
              } }
          : { connector_metadata: { selected_table_names: selectedIds } };
      const r = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connector.connector_id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(patchBody),
      });
      if (!r.ok) throw new Error('Failed to save selection');
      notify(selectedIds.length > 0
        ? `${selectedIds.length} ${selectedIds.length === 1 ? label.replace(/s$/, '') : label} saved`
        : `Nothing selected. ${connector.name || connector.type} will be skipped when the data model is built.`,
      'success');
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const renderResource = (r: Resource, opts?: { qualified?: boolean }) => {
    const name = resourceLabel(r);
    // Included rows sit under a schema header, so the prefix is redundant.
    // Excluded rows are a flat list at the bottom and need the full name.
    const displayName = !opts?.qualified && hasSchemas && name.includes('.')
      ? name.substring(name.indexOf('.') + 1)
      : name;
    const isSelected = selectedIds.includes(r.id);
    const isFolder = isDrive && r.mimeType === 'application/vnd.google-apps.folder';
    const ItemIcon = isDrive
      ? (isFolder ? FolderRounded : InsertDriveFileRounded)
      : TableChartRounded;
    const iconColor = isDrive
      ? (isFolder ? 'warning.main' : (isSelected ? 'primary.main' : 'text.disabled'))
      : (isSelected ? 'primary.main' : 'text.disabled');
    return (
      <Box
        key={r.id}
        onClick={() => toggle(r.id)}
        sx={(t) => ({
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1, py: 0.75, borderRadius: `${t.ds.radius.sm}px`, cursor: 'pointer',
          bgcolor: isSelected ? t.tokens.brand.primarySoft : 'transparent',
          '&:hover': { bgcolor: isSelected ? t.tokens.brand.primarySoft : t.tokens.surface.sunken },
        })}
      >
        <Checkbox checked={isSelected} size="small" disableRipple sx={{ p: 0 }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          onChange={() => toggle(r.id)} />
        <ItemIcon sx={{ fontSize: 16, color: iconColor, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: isSelected ? 500 : 400 }} noWrap>
          {displayName}
        </Typography>
        {isDrive ? (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem', flexShrink: 0 }}>
            {isFolder ? 'Folder' : 'File'}
          </Typography>
        ) : r.type ? (
          <Chip label={r.type} size="small" variant="outlined"
            sx={(t) => ({ fontSize: '0.6875rem', height: 20, borderRadius: `${t.ds.radius.sm}px`, flexShrink: 0 })} />
        ) : null}
      </Box>
    );
  };

  const titleLabel = isDrive ? 'Select files & folders' : `Select ${label}`;
  const subtitleParts = [
    connector?.name || connector?.type,
    `${resources.length} available`,
    `${selectedIds.length} selected`,
    autoInclude && resources.length > selectedIds.length
      ? `${resources.length - selectedIds.length} excluded`
      : null,
  ].filter(Boolean);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      // Pin the dialog to its full height rather than letting it size to
      // content: ticking a row moves it between the two columns, and a
      // content-sized dialog would jump every time. On mobile AppDialog goes
      // fullscreen and sets its own height, so leave that alone.
      paperSx={{ height: { xs: undefined, sm: 'min(92vh, 880px)' } }}
      icon={connector ? <ConnectorIcon type={connector.type} size={20} /> : undefined}
      title={titleLabel}
      subtitle={subtitleParts.join(' · ')}
      busy={saving}
      contentSx={{ px: 0, py: 0, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
      primaryAction={{
        label: saving ? 'Saving…' : `Save${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`,
        onClick: handleSave,
        // Nothing ticked is a real choice: the connector is skipped in builds.
        disabled: loading,
      }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
      actionsLeftExtra={
        <Typography variant="caption" color="text.secondary">
          {selectedIds.length > 0
            ? `${selectedIds.length} ${selectedIds.length === 1 ? label.replace(/s$/, '') : label} selected`
            : `No ${label} selected. This connector will be skipped when the data model is built.`}
        </Typography>
      }
    >
      {/* Hint + search stay pinned; only the columns below scroll. */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: 0, flexShrink: 0 }}>
        {autoInclude && !loading && !error && resources.length > 0 && (
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mb: 1.25, fontSize: '0.75rem', lineHeight: 1.5 }}>
            Tables added to this database later are included automatically.
            Anything you untick here stays out until you tick it again.
          </Typography>
        )}
        {!loading && !error && resources.length > 0 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              placeholder={`Search ${label}…`}
              size="small"
              fullWidth
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              sx={{
                flex: { sm: '1 1 0%' },
                minWidth: 0,
                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8125rem', height: 36 },
              }}
            />
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-end', sm: 'flex-start' }} sx={{ flexShrink: 0 }}>
              <Button size="small" variant="outlined"
                onClick={() => {
                  const selectable = isDrive
                    ? filtered.filter(r => r.mimeType !== 'application/vnd.google-apps.folder')
                    : filtered;
                  // Union rather than replace: with a search active, replacing
                  // would silently drop (and under auto-include, permanently
                  // exclude) everything the search happens to hide.
                  setSelectedIds(prev => Array.from(new Set([...prev, ...selectable.map(r => r.id)])));
                }}
                sx={{ borderRadius: '7px', textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap', height: 36, px: 1.5 }}>
                Select All
              </Button>
              {selectedIds.length > 0 && (
                <Button size="small" variant="text"
                  onClick={() => {
                    const visible = new Set(filtered.map(r => r.id));
                    setSelectedIds(prev => prev.filter(id => !visible.has(id)));
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap', height: 36, px: 1, color: 'text.secondary' }}>
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>
        )}
        <Divider />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // The two columns own their own scrolling on desktop; on a narrow
          // screen they stack and this box scrolls them together.
          overflowY: autoInclude ? { xs: 'auto', sm: 'hidden' } : 'auto',
        }}
      >
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{error}</Alert>
        ) : filtered.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1 }}>
            <StorageRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {search ? 'No results found' : `No ${label} available`}
            </Typography>
          </Box>
        ) : autoInclude ? (
          /* Two columns: what syncs on the left, what the user has removed on
             the right. Each scrolls on its own, so a long included list can't
             push the excluded ones off the bottom of the dialog. */
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box
              sx={(t) => ({
                flex: 1,
                minWidth: 0,
                minHeight: { sm: 0 },
                overflowY: { xs: 'visible', sm: 'auto' },
                borderRight: { sm: `1px solid ${t.tokens.border.subtle}` },
                borderBottom: { xs: `1px solid ${t.tokens.border.subtle}`, sm: 'none' },
              })}
            >
              <PaneHeader
                title={`Included (${included.length})`}
                subtitle="Synced on every build."
              />
              {/* Rows carry 8px of their own padding for the hover
                  background, so the list sits 8px in from the heading to land
                  on the same left edge. */}
              {/* Flat and fully qualified, matching the excluded column. A
                  schema heading here would only restate the prefix each row
                  already carries. */}
              <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
                {included.length === 0 ? (
                  <PaneEmpty>
                    {search ? 'No matches here.' : `Nothing selected — tick a ${label.replace(/s$/, '')} on the right.`}
                  </PaneEmpty>
                ) : included.map(r => renderResource(r, { qualified: true }))}
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: { sm: 0 },
                overflowY: { xs: 'visible', sm: 'auto' },
              }}
            >
              <PaneHeader
                title={`Excluded (${excludedItems.length})`}
                subtitle="Left out of every build. Tick one to bring it back."
              />
              <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
                {excludedItems.length === 0 ? (
                  <PaneEmpty>
                    {search ? 'No matches here.' : `Nothing excluded — every ${label.replace(/s$/, '')} syncs.`}
                  </PaneEmpty>
                ) : excludedItems.map(r => renderResource(r, { qualified: true }))}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
            {schemaKeys.map(schemaKey => (
              <Box key={schemaKey}>
                {hasSchemas && (
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', px: 1, py: 0.75, display: 'block' }}>
                    {schemaKey}
                  </Typography>
                )}
                {groups[schemaKey].map(r => renderResource(r))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </AppDialog>
  );
}

// ---------------------------------------------------------------------------
// Build scope — what the run will actually cover
// ---------------------------------------------------------------------------

/**
 * One connector's line in the build confirmation.
 *
 * Repeats the connector list's badges verbatim so the confirmation and the
 * page behind it can never disagree about what is going into the build.
 */
function BuildScopeRow({
  conn,
  summary,
  label,
}: {
  conn: ConnectorFull;
  summary: Summary;
  label: string;
}) {
  // Nothing selected: left out of the build, and said so where the counts go.
  const skipped = !summary.ready;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.25 }}>
      <ConnectorIcon type={conn.type} size={16} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={500} noWrap
          color={skipped ? 'text.secondary' : undefined} sx={{ fontSize: '0.8125rem' }}>
          {conn.name || conn.type}
        </Typography>
        <Typography variant="caption" color="text.secondary"
          sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
          {conn.type.replace(/_/g, ' ')}
        </Typography>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 96px)',
          gap: 0.625,
          alignItems: 'center',
        }}
      >
        {skipped ? (
          <Tooltip title={`No ${label} selected in this connector, so the build leaves it out`}>
            <Chip
              label="Skipped · nothing selected"
              size="small"
              variant="outlined"
              sx={(t) => ({
                ...countChipSx(t),
                // The Total and Selected slots exactly, so both edges line up
                // with the badges on the rows above and below.
                gridColumn: '1 / 3',
                width: '100%',
                color: 'text.secondary',
                bgcolor: t.tokens.surface.raised,
              })}
            />
          </Tooltip>
        ) : (
          <SelectionChips summary={summary} label={label} layout="grid" />
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Pipeline steps explainer
// ---------------------------------------------------------------------------

function PipelineExplainer({ connectorCount, skippedCount = 0, loading }: {
  connectorCount: number; skippedCount?: number; loading?: boolean;
}) {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const steps = [
    { icon: <RefreshRounded sx={{ fontSize: 20 }} />, label: 'Refresh Schema', desc: 'Fetch the latest schema of the selected tables from each connector', color: theme.palette.primary.main, ai: false },
    { icon: <TableChartRounded sx={{ fontSize: 20 }} />, label: 'Profile Tables', desc: 'Compute statistics and data quality metrics', color: theme.palette.warning.main, ai: false },
    { icon: <AccountTreeRounded sx={{ fontSize: 20 }} />, label: 'Build Data Model', desc: 'AI detects what changed and applies updates incrementally, preserving your edits', color: theme.palette.success.main, ai: true },
  ];
  return (
    <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', mb: 2.5, bgcolor: t.tokens.surface.sunken, borderColor: t.tokens.border.subtle })}>
      {/* Section header with divider */}
      <Box sx={(t) => ({
        px: { xs: 1.75, sm: 2 }, py: 1.25,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
        display: 'flex', alignItems: 'center', gap: 1,
      })}>
        <SyncRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" fontWeight={600} color="text.secondary"
          sx={{ letterSpacing: 0.3, fontSize: '0.75rem' }}>
          What happens when you run
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 1.75, sm: 2 } }}>
        <Stack direction={isMdDown ? 'column' : 'row'} spacing={0}>
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.75, minWidth: 0 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${s.color}18`, color: s.color, border: `1.5px solid ${s.color}40` }}>
                  {s.icon}
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center">
                  <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3 }}>{s.label}</Typography>
                  {s.ai && <AutoAwesomeRounded sx={{ fontSize: 11, color: 'primary.main' }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: '0.75rem', px: { xs: 0.5, sm: 0 } }}>{s.desc}</Typography>
              </Box>
              {i < steps.length - 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    py: isMdDown ? 0.5 : 0,
                    pt: isMdDown ? 0 : 2.25,
                  }}
                >
                  <ArrowForwardIosRounded
                    sx={{
                      fontSize: 12,
                      color: 'text.disabled',
                      mx: isMdDown ? 0 : 1,
                      transform: isMdDown ? 'rotate(90deg)' : 'none',
                    }}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Box>
      {/* Scope note - sits as a quiet footer strip inside the card */}
      <Box sx={(t) => ({
        px: { xs: 1.75, sm: 2 }, py: 1,
        borderTop: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1,
      })}>
        <StorageRounded sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
          Runs across{' '}
          {loading
            ? <Skeleton variant="text" width={110} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
            : skippedCount > 0
              ? <><strong>{connectorCount - skippedCount} of {connectorCount} active connectors</strong>; a connector with nothing selected is skipped.</>
              : <><strong>all {connectorCount} active connector{connectorCount !== 1 ? 's' : ''}</strong>.</>}
          {' '}To remove a connector altogether,{' '}
          <Button size="small" variant="text" onClick={() => navigate('/connectors')} sx={{ p: 0, minWidth: 'unset', fontSize: '0.75rem', fontWeight: 600, verticalAlign: 'baseline' }}>
            deactivate it first →
          </Button>
        </Typography>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function DataSyncPage() {
  const { shouldShow, currentStepId } = useProductOnboarding();
  const navigate = useNavigate();
  const { requireClaim } = useClaimGate();
  const { notify } = React.useContext(SnackbarContext);
  const { notifyJobUpdate, runningJob: activeSyncJob } = React.useContext(SyncJobContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [connectors, setConnectors] = React.useState<ConnectorFull[]>([]);
  const [loadingConnectors, setLoadingConnectors] = React.useState(true);
  const [domainContext, setDomainContext] = React.useState('');
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState<string | null>(null);

  // Domain context modal
  const [domainModalOpen, setDomainModalOpen] = React.useState(false);
  const [domainDraft, setDomainDraft] = React.useState('');
  const [savingDomain, setSavingDomain] = React.useState(false);
  const [rebuildFromScratch, setRebuildFromScratch] = React.useState(false);

  // Generic table/resource picker modal (used for SQL + REST connectors)
  const [pickerConnector, setPickerConnector] = React.useState<ConnectorFull | null>(null);
  // Klaris-owned Drive files modal (handles add/remove + per-file sheet selection)
  const [driveModalConnector, setDriveModalConnector] = React.useState<ConnectorFull | null>(null);
  const buildModelBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [editBtnAnchor, setEditBtnAnchor] = React.useState<HTMLButtonElement | null>(null);

  // History
  const [history, setHistory] = React.useState<SyncJob[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const authHeader = React.useCallback((): Record<string, string> => {
    const token = localStorage.getItem('klaris_jwt');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  React.useEffect(() => {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return;
    try { const t = JSON.parse(raw); setTenantId(t.tenant_id || t.id || null); }
    catch { setTenantId(raw); }
  }, []);

  const loadConnectors = React.useCallback(() => {
    if (!tenantId) return;
    setLoadingConnectors(true);
    fetch(
      buildApiUrl(`/api/v1/tenants/${tenantId}/connectors?include_table_selection=true`),
      { headers: authHeader() as HeadersInit },
    )
      .then(r => r.json())
      .then((data: any) => {
        const active: ConnectorFull[] = (Array.isArray(data) ? data : data.connectors || []).filter(
          (c: ConnectorFull) => c.status === 'active',
        );
        setConnectors(active);
      })
      .catch(e => console.error('Failed to load connectors', e))
      .finally(() => setLoadingConnectors(false));
  }, [tenantId, authHeader]);

  // Open Klaris-owned Drive picker. The modal itself launches Google's native
  // picker as a sub-flow when the user wants to add new files, but the
  // canonical selection state (including remove + per-file sheet picks) lives
  // in our UI — fixing the long-standing UX gap where the native picker was
  // write-only and removal required visiting the connector detail page.
  const handleDriveEdit = React.useCallback((conn: ConnectorFull) => {
    setDriveModalConnector(conn);
  }, []);

  const loadHistory = React.useCallback(() => {
    if (!tenantId) return;
    setHistoryLoading(true);
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs?limit=5`), { headers: authHeader() as HeadersInit })
      .then(r => r.json())
      .then((data: SyncJob[]) => setHistory(Array.isArray(data) ? data : []))
      .catch(e => console.error('Failed to load sync history', e))
      .finally(() => setHistoryLoading(false));
  }, [tenantId, authHeader]);

  React.useEffect(() => { loadConnectors(); }, [loadConnectors]);

  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  // Load saved domain context from tenant properties
  React.useEffect(() => {
    if (!tenantId) return;
    fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/properties`), { headers: authHeader() as HeadersInit })
      .then(r => r.ok ? r.json() : { properties: {} })
      .then(data => {
        const saved = data?.properties?.domain_context || '';
        setDomainContext(saved);
      })
      .catch(() => {});
  }, [tenantId, authHeader]);

  /**
   * Per-connector selection summary.
   *
   * Prefers the live counts the backend resolved against the source, which
   * already account for tables added since the last build. Falls back to the
   * saved metadata for file/REST connectors and for any source that could not
   * be reached. A connector is "ready" when it has something to sync.
   */
  const summarise = React.useCallback((conn: ConnectorFull): Summary => {
    const live = conn.table_selection;
    if (live) {
      return {
        total: live.available,
        count: live.selected,
        excluded: live.excluded,
        allTables: false,
        ready: live.selected > 0,
      };
    }
    return {
      total: null,
      count: selectionCount(conn),
      excluded: exclusionCount(conn),
      allTables: includesAllTables(conn),
      ready: isReady(conn),
    };
  }, []);

  const isConnectorReady = React.useCallback(
    (c: ConnectorFull) => summarise(c).ready, [summarise],
  );
  // The connectors that go into the build: the ones with something selected.
  const buildable = connectors.filter(isConnectorReady);
  const canBuild = buildable.length > 0;
  const skippedCount = connectors.length - buildable.length;
  const firstUnreadyConnectorId = connectors.find((c: ConnectorFull) => !isConnectorReady(c))?.connector_id;
  const showOwnDataNudges = shouldShow && currentStepId === 'data_model_built';

  // After connectors render, imperatively find the edit-button anchor for the nudge
  React.useEffect(() => {
    if (!showOwnDataNudges || canBuild) { setEditBtnAnchor(null); return; }
    const el = document.querySelector<HTMLButtonElement>('[data-nudge="select-tables"]');
    setEditBtnAnchor(el);
  }, [connectors, showOwnDataNudges, canBuild]);

  const handleStartSync = async (contextOverride?: string) => {
    if (!tenantId || !canBuild) return;
    setStarting(true);
    setStartError(null);
    const effectiveContext = contextOverride !== undefined ? contextOverride : domainContext;
    try {
      const r = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          // Only connectors with something selected; the rest are skipped.
          connector_ids: buildable.map((c: ConnectorFull) => c.connector_id),
          domain_context: effectiveContext.trim() || null,
          rebuild_from_scratch: rebuildFromScratch,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error((err as any).detail || `Server error ${r.status}`);
      }
      const job: SyncJob = await r.json();
      notifyJobUpdate(job);
      navigate(`/data-sync/${job.job_id}`);
    } catch (e: any) {
      setStartError(e.message || 'Failed to start sync');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 2, md: 0 } }}>
      {/* Page header */}
      <Box sx={(t) => ({ mb: 2.5, pb: 1.5, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 2, md: 0 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5, flex: { md: 1 }, minWidth: 0 }}>
            Refreshes schema, profiles tables, then uses <strong>AI</strong> to update your data model incrementally
          </Typography>
          <Tooltip
            title={
              activeSyncJob ? 'A sync is already running - wait for it to finish' :
              !canBuild ? 'Select tables in at least one connector to build the data model' :
              skippedCount > 0 ? `${skippedCount} connector${skippedCount !== 1 ? 's have' : ' has'} nothing selected and will be skipped` : ''
            }
            placement="left"
          >
            <span style={{ display: 'block', width: isMobile ? '100%' : 'auto' }}>
              <Button
                ref={buildModelBtnRef}
                variant="contained"
                size="medium"
                fullWidth={isMobile}
                startIcon={starting ? <CircularProgress size={15} color="inherit" /> : <AccountTreeRounded />}
                onClick={() => {
                  if (!canBuild || starting || !!activeSyncJob) return;
                  // Anonymous users must claim their workspace before running
                  // the AI pipeline; real users proceed straight to the modal.
                  requireClaim('build_data_model', () => {
                    setDomainDraft(domainContext);
                    setRebuildFromScratch(false);
                    setDomainModalOpen(true);
                  });
                }}
                disabled={!canBuild || starting || !!activeSyncJob}
                sx={{ fontWeight: 700, px: 2.5, py: 0.875, fontSize: '0.875rem', borderRadius: 2 }}
              >
                {starting ? 'Starting…' : 'Build Data Model'}
              </Button>
            </span>
          </Tooltip>
          <OnboardingCoachmark
            open={Boolean(showOwnDataNudges && canBuild && !starting && !activeSyncJob)}
            anchorRef={buildModelBtnRef}
            message="Now click Build Data Model."
            placement="bottom-end"
          />
        </Stack>
      </Box>

      {/* Pipeline explainer (includes scope note) */}
      <PipelineExplainer connectorCount={connectors.length} skippedCount={skippedCount} loading={loadingConnectors} />

      {startError && !activeSyncJob && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{startError}</Alert>}

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2.5, md: 3 }, alignItems: 'stretch' }}>

        {/* Left: connectors + domain context */}
        <Box sx={{ flex: '1 1 0', minWidth: 0, order: { xs: 1, md: 1 } }}>

          {/* Connector readiness table */}
          <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', borderColor: t.tokens.border.subtle, bgcolor: t.tokens.surface.raised })}>
            <Box sx={(t) => ({
              px: { xs: 1.5, sm: 2 }, py: 1,
              borderBottom: `1px solid ${t.tokens.border.default}`,
              bgcolor: t.tokens.surface.sunken,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 0.75, sm: 0 },
            })}>
              <Typography variant="caption" fontWeight={600} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
                Active Connectors{loadingConnectors ? '' : ` (${connectors.length})`}
              </Typography>
              {loadingConnectors ? (
                <Skeleton variant="text" width={72} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {buildable.length} / {connectors.length} ready{skippedCount > 0 ? ` · ${skippedCount} skipped` : ''}
                </Typography>
              )}
            </Box>

            {/* The list endpoint queries each SQL source for its table counts,
                so this card is the one part of the page that waits. Shimmer
                rows keep the layout stable rather than collapsing it. */}
            {loadingConnectors ? (
              [0, 1].map((i) => (
                <Box
                  key={i}
                  sx={(t) => ({
                    display: 'flex', alignItems: 'center', gap: 2,
                    px: { xs: 1.5, sm: 2 }, py: 1.5,
                    borderBottom: i === 0 ? `1px solid ${t.tokens.border.subtle}` : 'none',
                  })}
                >
                  <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width={i === 0 ? 190 : 150} height={20} />
                    <Skeleton variant="text" width={70} height={16} />
                  </Box>
                  <Skeleton variant="rounded" width={108} height={28} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
                </Box>
              ))
            ) : connectors.length === 0 ? (
              <Box sx={{ px: 2.5, py: 5, textAlign: 'center' }}>
                <StorageRounded sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No active connectors.</Typography>
                <Link
                  component={RouterLink}
                  to="/connectors"
                  underline="hover"
                  sx={{
                    mt: 1,
                    display: 'inline-block',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                  }}
                >
                  Add connector →
                </Link>
              </Box>
            ) : connectors.map((conn: ConnectorFull, idx: number) => {
              const label = selectionLabel(conn);
              const summary = summarise(conn);
              const ready = summary.ready;
              const isDrive = conn.type === 'google_drive';
              return (
                <Box
                  key={conn.connector_id}
                  sx={(t) => ({
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 1.25, sm: 2 },
                    px: { xs: 1.5, sm: 2 }, py: 1.5,
                    borderBottom: idx < connectors.length - 1 ? `1px solid ${t.tokens.border.subtle}` : 'none',
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: { sm: 1 } }}>
                    <ConnectorIcon type={conn.type} size={20} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Name row — connector name with the selection-count
                          badge inline. Pulls the count out of the action
                          button on the right so the row reads as
                          "<name>: 3 tables" left-to-right, and the action
                          button on the right collapses to a clean verb
                          ("Edit Tables") rather than a noun-count. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          title={conn.name || conn.type}
                          sx={{ minWidth: 0 }}
                        >
                          {conn.name || conn.type}
                        </Typography>
                        <SelectionChips summary={summary} label={label} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {conn.type.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  </Box>

                  {/*
                   * Selection affordance.
                   *
                   * Status (count) is shown as a badge next to the connector
                   * name on the left. The right-hand button is purely an
                   * action verb so users always know what clicking it does.
                   *
                   * Ready  → neutral outlined "Edit Tables/Files/Resources"
                   *          (count lives in the chip on the left)
                   * Empty  → contained primary "Select Tables/Files/Resources"
                   *          (prominent verb-driven CTA)
                   */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      display: 'flex',
                      justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                    }}
                  >
                    {ready ? (
                      <Tooltip title={`Change which ${label} sync from this connector`}>
                        <Button
                          data-nudge={showOwnDataNudges && !canBuild && conn.connector_id === firstUnreadyConnectorId ? 'select-tables' : undefined}
                          variant="outlined"
                          size="small"
                          color="primary"
                          disableElevation
                          startIcon={<EditRounded sx={{ fontSize: '14px !important' }} />}
                          onClick={() => isDrive ? handleDriveEdit(conn) : setPickerConnector(conn)}
                          sx={(t) => ({
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            borderRadius: `${t.ds.radius.sm}px`,
                            px: 1.25,
                            py: 0.375,
                            minHeight: 28,
                            // The global MuiButton.outlined override in the
                            // theme forces neutral border + text colour, so
                            // we have to re-assert brand-primary here. The
                            // count/status lives in the chip on the left,
                            // which frees this button to be loud and
                            // unmistakably clickable.
                            color: 'primary.main',
                            borderColor: t.tokens.brand.primary,
                            '&:hover': {
                              color: 'primary.main',
                              borderColor: t.tokens.brand.primaryHover ?? t.tokens.brand.primary,
                              bgcolor: alpha(t.tokens.brand.primary, 0.06),
                            },
                          })}
                        >
                          Edit {label.charAt(0).toUpperCase() + label.slice(1)}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip title={`Select ${label} to sync from this connector`}>
                        <Button
                          data-nudge={showOwnDataNudges && !canBuild && conn.connector_id === firstUnreadyConnectorId ? 'select-tables' : undefined}
                          variant="contained"
                          size="small"
                          color="primary"
                          disableElevation
                          startIcon={<WarningAmberRounded sx={{ fontSize: '15px !important' }} />}
                          onClick={() => isDrive ? handleDriveEdit(conn) : setPickerConnector(conn)}
                          sx={(t) => ({
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            borderRadius: `${t.ds.radius.sm}px`,
                            px: 1.5,
                            py: 0.375,
                            minHeight: 28,
                          })}
                        >
                          Select {label.charAt(0).toUpperCase() + label.slice(1)}
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Paper>

        </Box>

        {/* Right: past runs */}
        <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, order: { xs: 2, md: 2 } }}>
          <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds.radius.md}px`, overflow: 'hidden', borderColor: t.tokens.border.subtle, bgcolor: t.tokens.surface.raised })}>
            <Box sx={(t) => ({
              px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: `1px solid ${t.tokens.border.default}`,
              bgcolor: t.tokens.surface.sunken,
              display: 'flex', alignItems: 'center', gap: 1,
            })}>
              <HistoryRounded sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="caption" fontWeight={600} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
                Past Runs
              </Typography>
            </Box>
            {historyLoading ? (
              <Box sx={{ px: 2, py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
            ) : history.length === 0 ? (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">No past runs yet</Typography>
              </Box>
            ) : history.map((j: SyncJob, idx: number) => {
              const durMs = j.completed_at
                ? parseUtcDate(j.completed_at).getTime() - parseUtcDate(j.created_at).getTime()
                : null;
              return (
                <Box key={j.job_id} onClick={() => navigate(`/data-sync/${j.job_id}`)}
                  sx={(t) => ({
                    px: { xs: 1.5, sm: 2 }, py: 1.5, cursor: 'pointer',
                    borderBottom: idx < history.length - 1 ? `1px solid ${t.tokens.border.subtle}` : 'none',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    minWidth: 0,
                    '&:hover': { bgcolor: t.tokens.surface.sunken },
                  })}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                      <StatusChip status={j.status} />
                      <Typography variant="caption" color="text.secondary">
                        {j.connector_ids.length} connector{j.connector_ids.length !== 1 ? 's' : ''}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatRelativeTime(j.created_at)}
                      {durMs !== null && ` · ${formatDuration(durMs)}`}
                    </Typography>
                  </Box>
                  <ArrowForwardIosRounded sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
                </Box>
              );
            })}
          </Paper>
        </Box>
      </Box>
      <OnboardingCoachmark
        open={Boolean(showOwnDataNudges && !canBuild && editBtnAnchor && !pickerConnector && !driveModalConnector)}
        anchorEl={editBtnAnchor}
        message="Select tables first."
        placement="top-end"
        maxWidth={300}
      />

      {/* Inline picker modal (SQL / REST connectors) */}
      <TablePickerModal
        open={!!pickerConnector}
        connector={pickerConnector}
        tenantId={tenantId || ''}
        authHeader={authHeader}
        onClose={() => setPickerConnector(null)}
        onSaved={loadConnectors}
      />

      {/* Klaris-owned Drive files modal (add/remove + per-file sheet picker) */}
      {driveModalConnector && (
        <DriveFilesPickerModal
          open={!!driveModalConnector}
          connectorId={driveModalConnector.connector_id}
          connectorName={driveModalConnector.name}
          tenantId={tenantId || ''}
          authHeader={authHeader}
          initialFiles={(driveModalConnector.connector_metadata?.selected_drive_files || []) as DriveFile[]}
          onClose={() => setDriveModalConnector(null)}
          onSaved={loadConnectors}
        />
      )}

      <AppDialog
        open={domainModalOpen}
        onClose={() => setDomainModalOpen(false)}
        size="md"
        icon={<AccountTreeRounded sx={{ color: 'primary.main' }} />}
        title="Build data model"
        subtitle={`${skippedCount > 0
          ? `Runs on ${buildable.length} of ${connectors.length} active connectors. ${
            connectors.filter((c) => !isConnectorReady(c)).map((c) => c.name || c.type).join(', ')
          } ${skippedCount === 1 ? 'has' : 'have'} nothing selected and ${skippedCount === 1 ? 'is' : 'are'} skipped.`
          : `Runs on all ${connectors.length} active connector${connectors.length !== 1 ? 's' : ''}.`
        } Refreshes the schema, profiles the tables, then AI applies incremental updates while preserving your edits.`}
        busy={starting}
        primaryAction={{
          label: starting ? 'Starting…' : 'Start build',
          icon: <AccountTreeRounded />,
          onClick: async () => {
            if (!tenantId) return;
            setSavingDomain(true);
            try {
              await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/properties`), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ properties: { domain_context: domainDraft.trim() } }),
              });
              setDomainContext(domainDraft.trim());
            } catch { /* non-blocking */ } finally {
              setSavingDomain(false);
            }
            setDomainModalOpen(false);
            handleStartSync(domainDraft.trim());
          },
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setDomainModalOpen(false) }}
      >
        {/* Scope first: what this run covers, connector by connector, before
            the optional context box. Expandable to the table names so a
            missing table is spotted here rather than after the build. */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <StorageRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" fontWeight={600} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
            What this build covers
          </Typography>
        </Stack>
        <Paper
          variant="outlined"
          sx={(t) => ({
            borderRadius: `${t.ds.radius.md}px`,
            borderColor: t.tokens.border.subtle,
            bgcolor: t.tokens.surface.sunken,
            mb: 2,
            maxHeight: 260,
            overflowY: 'auto',
          })}
        >
          {[...buildable, ...connectors.filter((c) => !isConnectorReady(c))].map((conn: ConnectorFull, idx: number) => (
            <Box
              key={conn.connector_id}
              sx={(t) => ({
                borderBottom: idx < connectors.length - 1
                  ? `1px solid ${t.tokens.border.subtle}` : 'none',
              })}
            >
              <BuildScopeRow
                conn={conn}
                summary={summarise(conn)}
                label={selectionLabel(conn)}
              />
            </Box>
          ))}
        </Paper>

        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <AutoAwesomeRounded sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography variant="caption" fontWeight={600} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
            AI Context <Typography component="span" variant="caption" color="text.disabled">(optional)</Typography>
          </Typography>
        </Stack>
        <TextField
          fullWidth multiline minRows={3} maxRows={8}
          placeholder={'Describe your business to help AI build better models - e.g. "E-commerce platform selling apparel. Orders link to customers and products…"'}
          value={domainDraft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDomainDraft(e.target.value)}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8125rem' } }}
        />
        <Box
          sx={{ display: 'flex', alignItems: 'center', mt: 2, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setRebuildFromScratch(v => !v)}
        >
          <Checkbox
            checked={rebuildFromScratch}
            onChange={e => setRebuildFromScratch(e.target.checked)}
            size="small"
            color="error"
            sx={{ p: 0, mr: 1 }}
            onClick={e => e.stopPropagation()}
          />
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
            Rebuild from scratch
          </Typography>
        </Box>
        {rebuildFromScratch && (
          <Alert severity="warning" icon={<WarningAmberRounded fontSize="small" />} sx={{ mt: 1.5, borderRadius: 2, fontSize: '0.8125rem' }}>
            The AI will re-analyse all your data sources and build a brand-new data model. Your existing entities, fields, relationships, and manual edits will be overwritten by the new model. This cannot be undone.
          </Alert>
        )}
      </AppDialog>
    </Box>
  );
}
